// Silo Art V2 — provider + global background layer + dashboard band.
//
// The provider fetches the creator's silos (for cube face images) + their V2
// prefs, tracks the "leader" silo as they move around, and exposes descriptors
// the background/band render. Everything is CSS on EXISTING images — no
// generation, no server work per page.
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, getToken } from "@/lib/api";
import { useDarkMode } from "@/lib/darkMode";
import {
  normalizeV2, faceForPath, pathIsThemed, faceImageUrl, optOutKeyForPath,
  normalizeZones,
} from "@/lib/siloArt";
import { setSiloCubeEnabled } from "@/lib/siloCube";
import { cropStyle } from "@/lib/siloCubeFaces";

const LEADER_LS = "mysilo_art_leader";
const THEME_SLUG_LS = "mysilo_theme_slug";
const Ctx = createContext(null);
export const useSiloArt = () => useContext(Ctx) || {};

export function SiloArtProvider({ children }) {
  const location = useLocation();
  const [mode, setModeState] = useState("v1");
  const [config, setConfigState] = useState(normalizeV2(null));
  const [silos, setSilos] = useState([]);
  const [leaderId, setLeaderId] = useState(() => {
    try { return localStorage.getItem(LEADER_LS) || null; } catch { return null; }
  });
  const [faceOverride, setFaceOverride] = useState(null);
  const [dark, setDark] = useState(() => {
    try { return document.documentElement.getAttribute("data-dark") === "1"; } catch { return false; }
  });
  const loaded = useRef(false);

  // Load prefs + silos once we're authenticated. Depends on the route so that
  // when the provider first mounts on a token-less page (e.g. /login) and the
  // user then signs in and navigates, the load fires — instead of never
  // retrying (which left V2 unthemed until a full reload on a fresh session).
  useEffect(() => {
    if (loaded.current) return;
    if (!getToken()) return;
    loaded.current = true;
    (async () => {
      try {
        const [artRes, silosRes] = await Promise.all([
          api.get("/me/silo-art").catch(() => null),
          api.get("/silos").catch(() => null),
        ]);
        if (artRes?.data) {
          // Oct 2026 (Randy) — stage two is the default now, not stage three.
          // Elevated art is for a cube that has been dressed; a brand-new
          // creator has dressed nothing, so landing them there showed them an
          // empty canvas and (until the fix above) white text on nothing.
          setModeState(artRes.data.mode || "v1");
          setConfigState(normalizeV2(artRes.data.config));
        }
        if (Array.isArray(silosRes?.data)) setSilos(silosRes.data);
      } catch { /* best-effort theming */ }
    })();
  }, [location.pathname]);

  // Track the leader silo as the creator enters silos.
  useEffect(() => {
    const p = location.pathname;
    let hit = null;
    const m = p.match(/^\/dashboard\/silo\/([^/]+)/);
    if (m) hit = m[1];
    else {
      const a = p.match(/^\/silo\/([^/]+)\/assistant/);
      const s = p.match(/^\/s\/([^/]+)/);
      const slug = a ? a[1] : (s ? s[1] : null);
      if (s) { try { localStorage.setItem(THEME_SLUG_LS, s[1]); } catch { /* */ } }
      if (slug && silos.length) hit = (silos.find((x) => x.slug === slug) || {}).id || null;
    }
    if (hit && hit !== leaderId) {
      setLeaderId(hit);
      try { localStorage.setItem(LEADER_LS, hit); } catch { /* */ }
    }
    setFaceOverride(null); // reset per-tab override on navigation
  }, [location.pathname, silos]); // eslint-disable-line react-hooks/exhaustive-deps

  const setMode = useCallback(async (next) => {
    setModeState(next);
    setSiloCubeEnabled(next !== "off");
    try {
      await api.patch("/me/silo-art", { mode: next });
      // Feb 17, 2026 (Randy pt-public-qa-live-mode) — announce a persisted
      // mode change so pages that cache a server-derived flag (e.g.
      // PublicSilo's `silo.silo_art_v2`) can refetch AFTER the backend has
      // durably applied the change. Firing before the PATCH resolves
      // caused a race where the refetch pulled the OLD flag.
      window.dispatchEvent(new CustomEvent("mysilo:silo-art-mode-persisted", { detail: { mode: next } }));
    } catch { /* local applied */ }
  }, []);

  const setConfig = useCallback(async (next) => {
    setConfigState(next);
    // Iter160m (Randy pt-siloart-savebtn-ux, Feb 2026) — toggles inside
    // Silo Art V2 (Bolden, All-white text, Ignore dark mode, etc.) call
    // this setter. Prior behavior fired silently — Randy: "there is NO
    // toast or any confirmation that anything is saving." Now the auto-
    // save (per-user config in /me/silo-art) surfaces a compact toast on
    // success + a clear error toast on failure, matching every other
    // save on the platform.
    try {
      await api.patch("/me/silo-art", { config: next });
      toast.success("Silo art preference saved.", { duration: 1400 });
    } catch {
      toast.error("Couldn't save that preference — please retry.");
    }
  }, []);

  // Re-pull silos after a per-silo LOOK save so live theming updates instantly.
  const refreshSilos = useCallback(async () => {
    try { const r = await api.get("/silos"); if (Array.isArray(r?.data)) setSilos(r.data); } catch { /* */ }
  }, []);

  const leaderSilo = useMemo(() => {
    if (config.leader_mode === "pinned" && config.leader_pin_silo_id) {
      return silos.find((s) => s.id === config.leader_pin_silo_id) || silos[0] || null;
    }
    return silos.find((s) => s.id === leaderId) || silos[0] || null;
  }, [silos, leaderId, config.leader_mode, config.leader_pin_silo_id]);

  useEffect(() => {
    if (leaderSilo && leaderSilo.slug) { try { localStorage.setItem(THEME_SLUG_LS, leaderSilo.slug); } catch { /* */ } }
  }, [leaderSilo]);

  const activeFace = faceOverride || faceForPath(location.pathname);

  const descriptorFor = useCallback((zoneKey, faceId) => {
    const zones = normalizeZones(leaderSilo && leaderSilo.silo_art);
    const z = zones[zoneKey] || zones.page;
    const facesMap = (leaderSilo && leaderSilo.cube && leaderSilo.cube.faces) || {};
    const useFace = (z.face && (facesMap[z.face] || {}).image_id) ? z.face : faceId;
    return {
      source: z.source,
      filter: z.filter,
      dim: z.dim,
      color: z.color,
      imageUrl: leaderSilo ? faceImageUrl(leaderSilo, useFace) : null,
      crop: (facesMap[useFace] || {}).crop || null,
    };
  }, [leaderSilo]);

  const optKey = optOutKeyForPath(location.pathname);
  const notOptedOut = !optKey || (config.pages && config.pages[optKey] !== false);
  // Feb 17, 2026 (Randy pt-feature-page-model-b) — per-page kill switch
  // that FORCES art off on this feature page regardless of the dashboard
  // slider. Default false (follow master). Only relevant on feature
  // pages (optKey present).
  const featurePageKeepOff = (() => {
    if (!optKey || !leaderSilo) return false;
    const fx = (normalizeZones(leaderSilo.silo_art).page_fx || {})[optKey] || {};
    return fx.keep_art_off === true;
  })();
  // Feb 17, 2026 (Randy pt-feature-page-model-b, iter2) — V1 mode is the
  // "cube cards" experience: cube-face tiles on the creator dashboard,
  // NO whole-page canvas paint on dashboard / manage / account / battles /
  // fan portal. V2 mode adds the elevated whole-page canvas everywhere.
  // Model B addition: the TWO feature pages (Advanced AI Assistant +
  // Public Q&A) get the canvas paint in BOTH V1 and V2 mode — Randy's
  // "V1 or V2 = feature pages get their silo art" rule. Everything else
  // stays V2-only.
  const isFeaturePage = optKey === "assistant" || optKey === "public_qa";
  const canvasAllowedInMode = mode === "v2" || (mode === "v1" && isFeaturePage);
  const themed = canvasAllowedInMode && !!leaderSilo && pathIsThemed(location.pathname) && notOptedOut && !featurePageKeepOff;
  // Oct 2026 (HOMEPAGE_PRICING_REVAMP.md §9) — IS THERE ACTUALLY ART BEHIND
  // THE WORDS? Randy found a creator account sitting on elevated art with
  // nothing dressed: the page forced white text over a bare cream background
  // and *"all of the canvas text was wiped out."* The recolour was gated on the
  // mode alone, never on whether an image resolved.
  //
  // His call, and the right one: suspend the effect, don't rewrite his
  // settings. The toggles are the owner's data — flipping them silently would
  // leave someone who dresses a cube next week wondering why white text never
  // came back. So the preference is kept exactly as saved and simply has no
  // effect until there is a picture to read it against. Judged per page, so a
  // half-dressed cube behaves correctly everywhere: dashboard dressed and
  // account not means the dashboard goes elevated and account stays readable.
  const artIsDressed = useMemo(() => {
    if (!leaderSilo) return false;
    const zone = normalizeZones(leaderSilo.silo_art).page;
    // A deliberate solid colour counts as dressed — the creator painted that
    // canvas on purpose and the recolour is reading against it.
    if (zone.source === "color") return true;
    if (zone.source === "off") return false;
    // Image zone: mirror faceImageUrl's resolution exactly, centre fallback
    // included, so "is there a picture" matches what actually renders.
    const faces = (leaderSilo.cube && leaderSilo.cube.faces) || {};
    const useFace = (zone.face && (faces[zone.face] || {}).image_id) ? zone.face : activeFace;
    return !!((faces[useFace] || {}).image_id || (faces.center || {}).image_id);
  }, [leaderSilo, activeFace]);

  // V2-only extras — bold body text, all-black text, whitetext overrides.
  // These stay strictly gated on V2 mode (the "full" theming). V1 mode
  // gets canvas paint on feature pages only, no body-class flair anywhere.
  const themedV2Extras = mode === "v2" && themed && artIsDressed;

  // Body-class hooks: canvas paint (V1 or V2) + V2-only body flair (bold /
  // all-black / whitetext). Feb 17 2026 (Randy pt-feature-page-model-b):
  // the "siloart-v2" body class stays tied to strict V2 so pages that key
  // off it (e.g. bolder headers) don't accidentally activate in V1 mode.
  // "siloart-canvas-on" is the SUPERSET class — true whenever the canvas
  // paints (V1 feature pages, all V2 pages). CSS that just needs to make
  // a surface transparent so the canvas shows through should key off
  // this class, not siloart-v2.
  useEffect(() => {
    document.body.classList.toggle("siloart-canvas-on", !!themed);
    document.body.classList.toggle("siloart-v2", !!themedV2Extras);
    document.body.classList.toggle("siloart-bold", !!themedV2Extras && !!config.bold_unboxed);
    document.body.classList.toggle("siloart-bold-mobile", !!themedV2Extras && !!config.bold_unboxed_mobile);
    document.body.classList.toggle("siloart-blacktext", !!themedV2Extras && !!config.black_text);
    document.body.classList.toggle("siloart-blacktext-mobile", !!themedV2Extras && !!config.black_text_mobile);
    // Feb 2026 (Randy pt-siloart-white-text) — mirror pair. Both classes
    // are gated on strict V2 so they cleanly no-op in V1 or Off mode.
    document.body.classList.toggle("siloart-whitetext", !!themedV2Extras && !!config.white_text);
    document.body.classList.toggle("siloart-whitetext-mobile", !!themedV2Extras && !!config.white_text_mobile);
    return () => {
      document.body.classList.remove("siloart-canvas-on");
      document.body.classList.remove("siloart-v2");
      document.body.classList.remove("siloart-bold");
      document.body.classList.remove("siloart-bold-mobile");
      document.body.classList.remove("siloart-blacktext");
      document.body.classList.remove("siloart-blacktext-mobile");
      document.body.classList.remove("siloart-whitetext");
      document.body.classList.remove("siloart-whitetext-mobile");
    };
  }, [themed, themedV2Extras, config.bold_unboxed, config.bold_unboxed_mobile, config.black_text, config.black_text_mobile, config.white_text, config.white_text_mobile]);

  // Track dark mode reactively. The site's dark mode toggles a `data-dark`
  // attribute on <html>; observing it (instead of a per-component hook) means
  // a live dark-mode toggle updates the V2 art instantly — no reload.
  useEffect(() => {
    const el = document.documentElement;
    const read = () => setDark(el.getAttribute("data-dark") === "1");
    read();
    const obs = new MutationObserver(read);
    obs.observe(el, { attributes: true, attributeFilter: ["data-dark"] });
    return () => obs.disconnect();
  }, []);

  const value = {
    mode, config, silos, leaderSilo, activeFace, dark,
    setMode, setConfig, setActiveFace: setFaceOverride, descriptorFor,
    refreshSilos, themed, themedV2Extras,
    // So the art panel can say WHY the text colours aren't doing anything yet.
    artIsDressed,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// ---- Rendering primitives ---------------------------------------------------

// Canvas pixelate — runs once per (url,blocks), returns a data URL.
const _pxCache = {};
function usePixelated(url, blocks = 44) {
  const [out, setOut] = useState(null);
  useEffect(() => {
    if (!url) { setOut(null); return; }
    const key = `${url}|${blocks}`;
    if (_pxCache[key]) { setOut(_pxCache[key]); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = blocks, h = Math.max(1, Math.round((img.height / img.width) * blocks));
        const c = document.createElement("canvas");
        c.width = w; c.height = h;
        const ctx = c.getContext("2d");
        ctx.imageSmoothingEnabled = true;
        ctx.drawImage(img, 0, 0, w, h);
        const data = c.toDataURL("image/png");
        _pxCache[key] = data; setOut(data);
      } catch { setOut(null); }
    };
    img.onerror = () => setOut(null);
    img.src = url;
  }, [url, blocks]);
  return out;
}

function cssFilter(filter) {
  switch (filter) {
    case "grayscale": return "grayscale(1) contrast(1.05)";
    case "blur": return "blur(7px)";
    case "vivid": return "saturate(1.7) contrast(1.12)";
    default: return "none";
  }
}

// A single art layer (used by both the page background and the dashboard band).
export function SiloArtSurface({ descriptor, dark, className = "", rounded = false, testid }) {
  const d = descriptor || {};
  const pixelated = usePixelated(d.filter === "pixelate" ? d.imageUrl : null);
  const showColor = d.source === "color" || !d.imageUrl;
  const dimRGB = dark ? "10,10,8" : "253,252,248";
  const src = d.filter === "pixelate" ? pixelated : d.imageUrl;
  // Honour the per-face framing (zoom + pan) the creator set in "Frame the
  // image" — same crop the silo pills use — so the content canvas / band zoom
  // out any white edges just like the pills do.
  const cs = cropStyle(d.crop) || {};

  return (
    <div
      aria-hidden="true"
      data-testid={testid}
      className={`pointer-events-none overflow-hidden ${rounded ? "rounded-2xl" : ""} ${className}`}
      style={{ backgroundColor: showColor ? d.color : (dark ? "#0a0a08" : "#FDFCF8") }}
    >
      {!showColor && src && (() => {
        const img = (
          <img
            src={src}
            alt=""
            className="w-full h-full object-cover"
            style={{
              filter: d.filter === "pixelate" ? "none" : cssFilter(d.filter),
              transform: cs.transform || (d.filter === "blur" ? "scale(1.08)" : "none"),
              transformOrigin: cs.transformOrigin,
              objectPosition: cs.objectPosition,
              imageRendering: d.filter === "pixelate" ? "pixelated" : "auto",
              opacity: 1,
              transition: "opacity 400ms ease",
            }}
          />
        );
        // Jul 2026 (Randy) — per-page mobile art zoom. When set, a wrapper
        // scales the art on phones only (CSS media query). Below 100% the
        // sharp image steps back and a blurred over-scaled copy fills the
        // revealed edges — never white, never hard. Desktop keeps the crop.
        return typeof d.mobileZoom === "number"
          ? (
            <div className="w-full h-full relative overflow-hidden">
              {d.mobileZoom < 100 && (
                <img src={src} alt="" aria-hidden="true" className="siloart-blurfill absolute inset-0 w-full h-full object-cover" />
              )}
              <div className={`siloart-zoomwrap w-full h-full relative ${d.mobileZoom < 100 ? "siloart-zoomcontain" : ""}`} style={{ "--sa-zoom": d.mobileZoom / 100 }}>{img}</div>
            </div>
          )
          : img;
      })()}
      {/* readability dim toward the page base colour */}
      <div className="absolute inset-0" style={{ backgroundColor: `rgba(${dimRGB}, ${(d.dim || 0) / 100})` }} />
    </div>
  );
}

// Content-canvas background. Dropped INTO a themed page's root (a `relative`
// container) as an absolute layer that fills the page behind the content, so
// the leader's art visibly dresses the canvas the cards + text sit on. The
// enclosed feature components keep their own opaque backgrounds on top.
export function SiloArtCanvas({ className = "", pageKey }) {
  const { themed, descriptorFor, activeFace, config, dark, leaderSilo } = useSiloArt();
  if (!themed) return null;
  let descriptor = descriptorFor("page", activeFace);
  if (descriptor.source === "off") return null;
  // Per-page opacity override (Stage-2): AI Assistant / Public Q&A can hold
  // their own dim, else they inherit the content-canvas dim.
  if (pageKey && leaderSilo) {
    const zAll = normalizeZones(leaderSilo.silo_art);
    const ov = (zAll.page_dim || {})[pageKey];
    if (typeof ov === "number") descriptor = { ...descriptor, dim: ov };
    const fx = (zAll.page_fx || {})[pageKey] || {};
    if (typeof fx.zoom === "number") descriptor = { ...descriptor, mobileZoom: fx.zoom };
  }
  // Dark mode: keep the art in its TRUE colours (don't let the site-wide
  // invert recolour it) when the Stage-2 toggle is on (default).
  const keepColor = dark && (config || {}).dark_ignore_canvas !== false;
  return (
    <SiloArtSurface
      descriptor={descriptor}
      dark={dark && !keepColor}
      testid="silo-art-canvas"
      className={`absolute inset-0 z-[-1] ${keepColor ? "siloart-canvas-truecolor" : ""} ${className}`}
    />
  );
}

// Fan-facing canvas: dresses a PUBLIC page from the VIEWED silo's OWN stored
// look + art. Works for logged-out visitors (no leader/provider needed).
export function SiloArtCanvasFor({ silo, face, className = "", pageKey }) {
  const { dark } = useSiloArt();
  if (!silo) return null;
  const zAll = normalizeZones(silo.silo_art);
  const z = zAll.page;
  if (z.source === "off") return null;
  const facesMap = (silo.cube && silo.cube.faces) || {};
  const useFace = (z.face && (facesMap[z.face] || {}).image_id) ? z.face : face;
  const dimOverride = pageKey ? (zAll.page_dim || {})[pageKey] : null;
  const fx = pageKey ? ((zAll.page_fx || {})[pageKey] || {}) : {};
  const descriptor = {
    source: z.source, filter: z.filter,
    dim: (typeof dimOverride === "number") ? dimOverride : z.dim,
    color: z.color,
    imageUrl: faceImageUrl(silo, useFace),
    crop: (facesMap[useFace] || {}).crop || null,
    mobileZoom: (typeof fx.zoom === "number") ? fx.zoom : undefined,
  };
  // Dark mode: keep the art in its TRUE colours (creator's Stage-2 toggle, on by default).
  const keepColor = dark && silo.silo_art_dark_ignore_canvas !== false;
  return (
    <SiloArtSurface
      descriptor={descriptor}
      dark={dark && !keepColor}
      testid="silo-art-canvas"
      className={`absolute inset-0 z-[-1] ${keepColor ? "siloart-canvas-truecolor" : ""} ${className}`}
    />
  );
}

// Dashboard "above-the-line" band accent (its own zone/config). Face is passed
// by the host (leader-aware — follows the active tab/context). Can be OFF.
export function SiloArtBand({ faceId, className = "" }) {
  const { mode, leaderSilo, descriptorFor, dark, config } = useSiloArt();
  if (mode !== "v2" || !leaderSilo) return null;
  const d = descriptorFor("band", faceId);
  if (d.source === "off") return null;
  // Feb 19, 2026 (Randy Photo #4, iter3) — if the leader silo hasn't
  // actually assigned real band art (no imageUrl AND source isn't an
  // explicit color choice), bail out. Otherwise SiloArtSurface would
  // paint a near-black chrome fallback that swallows the hero card's
  // cream background. Cream now shows through until the leader configures
  // real art for the "band" zone.
  const hasRealArt = !!d.imageUrl || (d.source === "color" && !!d.color);
  if (!hasRealArt) return null;
  // Dark mode: keep the band art in its TRUE colours too (same Stage-2 toggle).
  const keepColor = dark && (config || {}).dark_ignore_canvas !== false;
  return (
    <SiloArtSurface
      descriptor={d}
      dark={dark && !keepColor}
      rounded
      testid="silo-art-band"
      className={`absolute inset-0 z-0 ${keepColor ? "siloart-canvas-truecolor" : ""} ${className}`}
    />
  );
}

// 3-position "Silo art" mode slider for the dashboard: left = Off,
// middle = V1 (cube cards), right = V2 (elevated). Click cycles. No words.
export function SiloArtModeSlider({ compact = false }) {
  const { mode, setMode } = useSiloArt();
  const idx = mode === "off" ? 0 : mode === "v1" ? 1 : 2;
  const next = idx === 0 ? "v1" : idx === 1 ? "v2" : "off";
  const label = mode === "off" ? "Silo art off — tap for cube cards" : mode === "v1" ? "Silo art: cube cards (V1) — tap for elevated" : "Silo art: elevated (V2) — tap to turn off";
  const knob = idx === 0 ? "translate-x-0.5" : idx === 1 ? "translate-x-[19px]" : "translate-x-[36px]";
  const btn = (
    <button
      type="button"
      onClick={(e) => { if (compact) e.stopPropagation(); setMode(next); }}
      role="switch"
      aria-checked={idx > 0}
      aria-label={label}
      title={label}
      data-testid="silo-art-mode-slider"
      data-mode={mode}
      className={`relative inline-flex h-5 w-[54px] items-center rounded-full transition-colors ${idx === 0 ? "bg-[#D8D5CD]" : "bg-[#4A5D4E]"}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${knob}`} />
    </button>
  );
  if (compact) return btn;
  return (
    <div className="mt-3 ml-8 sm:ml-10 inline-flex items-center gap-2" data-testid="silo-art-mode-slider-wrap">
      {/* Iter160m (Randy pt-siloart-loosetext-sweep) — dropped
          `!text-black` from this "SILO ART" toggle label. Now obeys
          Silo Art V2 whitetext/blacktext rules like every other loose
          label on the dashboard canvas. */}
      <span className="text-[10px] uppercase tracking-[0.22em] silo-loose">Silo art</span>
      {btn}
    </div>
  );
}
